import React from 'react';
import {string} from 'prop-types';
import {clone} from 'utils/DBUtils';
import ResourceForm from './ResourceForm';
import resourceField from 'hoc/ResourceField';
import resourceSection from 'hoc/ResourceSection';
import ViewSwitchSection from 'components/forms/ViewSwitchSection';
import resourceViewSwitchSection from 'hoc/ResourceViewSwitchSection';
import ConditionalSection from 'components/common/ConditionalSection';
import AutoSaveComponents from 'components/forms/Component Sets/AutoSaveComponents';

// ResourceForm subclass that implements non-multiplatform auto-save.
class AutoSaveResourceForm extends ResourceForm {

    makeComponent(field, fieldPath, resourcePath, sectionIndex, sectionKeyPath) {
        let Component = this.props.components[field.type];
        if (!Component) {
            // Can't render without a component
            console.warn(`Could not find component type '${field.type}' in config:`);
            console.warn(this.props.components);
            return null;
        }

        // Get dynamic props for field
        let fieldProps = this.fieldProps(field, fieldPath);

        const key = this.getUniqueComponentKey(field, resourcePath);
        const proxy = this.makeProxy(fieldPath, resourcePath);

        const ResourceFieldComp = props => props.showField !== false ? (
            <Component {...field.props}
                       {...fieldProps}
                       lang={this.props.resource.lang}
                       key={this.getUniqueComponentKey(field, resourcePath)}
                       fieldKey={this.getUniqueComponentKey(field, resourcePath)}
                       name={field.name}
                       isSaving={props.isSaving}
                       savedField={props.savedField}
                       errors={props.errors || this.props.errors[field.name]}
                       required={typeof(field.required) === typeof(true) || typeof(field.required) === 'undefined' ? field.required : field.required(proxy)}
                       sectionIndex={sectionIndex}
                       value={props.value}
                       disabled={props.disabled}
                       defaultValue={field.props.defaultValue}
                       showExtraContent={props.showExtraContent}
                       onValueChanged={props.onValueChanged || this.makeOnValueChanged(fieldPath, resourcePath)}
                       onValidationError={this.makeOnValidationError(fieldPath, resourcePath)}
                       formProxy={this.makeProxy(fieldPath, resourcePath)}/>
        ) : null;

        ResourceFieldComp.displayName = 'AutoSaveResourceField';

        const ResourceField = resourceField({
            resource: this.props.resource,
            fieldPath: fieldPath,
            resourcePath: resourcePath,
            showField: field.showField,
            autoPopulate: field.autoPopulate,
            showExtraContent: field.showExtraContent,
            formProxy: proxy,
            field: field,
            reducerKey: this.props.reducerKey,
            sectionKeyPath: sectionKeyPath
        }, ResourceFieldComp);

        return <ResourceField key={key} />;
    }

    makeViewSwitchSectionWrapper(section, viewCaseFields, fieldPath, resourcePath) {
        const formProxy = this.makeProxy(fieldPath, resourcePath);

        const ViewSwitchComp = props => (
            <ViewSwitchSection section={section}
                               formProxy={formProxy}
                               switchValue={section.viewSwitch(formProxy)}
                               switchCases={viewCaseFields} />
        );

        ViewSwitchComp.displayName = 'AutoSaveViewSwitchSection';

        const ResourceViewSwitchSection = resourceViewSwitchSection({
            section,
            formProxy
        }, ViewSwitchComp);

        return <ResourceViewSwitchSection key={section.key} />;
    }

    makeConditionalSectionWrapper(field, index, key, fieldPath, resourcePath, fields, sectionKeyPath) {
        const ResourceFieldComp = props => props.showField !== false ? (
            <ConditionalSection {...field.props}
                                index={index}
                                key={key}
                                sectionKey={key}
                                fieldKey={key}
                                name={field.name}
                                formProxy={this.makeProxy(fieldPath, resourcePath)}
                                value={this.valueForField(fieldPath)}
                                required={field.required}
                                onValueChanged={props.onValueChanged || this.makeOnValueChanged(fieldPath, resourcePath)}
                                conditionalValue={this.currentValueForField(fieldPath) || false}>
              {fields}
            </ConditionalSection>
        ) : null;

        ResourceFieldComp.displayName = 'AutoSaveResourceConditionalSection';

        const proxy = this.makeProxy(fieldPath, resourcePath);
        const ResourceField = resourceField({
            resource: this.props.resource,
            fieldPath: fieldPath,
            resourcePath: resourcePath,
            showField: field.showField,
            field: field,
            formProxy: proxy,
            reducerKey: this.props.reducerKey,
            sectionKeyPath: sectionKeyPath
        }, ResourceFieldComp);

        return <ResourceField key={key} />;
    }

    makeSectionWrapper(SectionWrapperComponent, key, section, sectionIndex, fieldPath, resourcePath, children, sectionGroup, sectionKeyPath) {
        const proxy = this.makeProxy(fieldPath, resourcePath);

        const ResourceSectionComp = props => props.showSection ? (
            <SectionWrapperComponent index={sectionIndex}
                                     formProxy={proxy}
                                     {...section.props}
                                     {...props}>
              {children}
            </SectionWrapperComponent>
        ) : null;

        const ResourceSection = resourceSection({
            reducerKey: this.props.reducerKey,
            openSection: this.props.openSection,
            sectionGroup: sectionGroup || null,
            sectionKey: key,
            sectionKeyPath: sectionKeyPath,
            section: section,
            sectionProps: section.props,
            formProxy: proxy,
            sectionIndex: sectionIndex
        }, ResourceSectionComp);

        ResourceSection.displayName = 'AutoSaveResourceSection';

        return <ResourceSection key={key} />;
    }
}

const basePropTypes = clone(ResourceForm.propTypes);
basePropTypes.reducerKey = string.isRequired;
AutoSaveResourceForm.propTypes = basePropTypes;

const baseDefaultProps = clone(ResourceForm.defaultProps);
baseDefaultProps.components = AutoSaveComponents;
AutoSaveResourceForm.defaultProps = baseDefaultProps;

export default AutoSaveResourceForm;



// WEBPACK FOOTER //
// ./public/components/forms/AutoSaveResourceForm.jsx